import {
  isCondition,
  isFamilyMemberHistory,
  isImmunization,
  isMedication,
  isObservation,
  isProcedure,
  SmartAllergyIntolerance,
  SmartDiagnosticReport,
} from "@metriport/fhir-sdk";
import {
  ConditionSeries,
  EnhancedMedicationSeries,
  ImmunizationSeries,
  MedicationSeries,
  ObservationSeries,
  ProcedureSeries,
} from "../table-transforms/series-utils";

/**
 * SHARED TYPE GUARDS for EHR write-back
 *
 * These type guards are used by both the validator and transformer
 * to detect V2 Series structures and raw FHIR resources.
 */

// ============================================================================
// SERIES TYPE GUARDS (V2 structures)
// ============================================================================

function isValidObject(data: unknown): data is object {
  return typeof data === "object" && data !== null;
}

export function isObservationSeries(data: unknown): data is ObservationSeries {
  if (!isValidObject(data)) return false;
  const series = data as ObservationSeries;
  return series.resourceType === "Observation" && Array.isArray(series.resources);
}

export function isConditionSeries(data: unknown): data is ConditionSeries {
  if (!isValidObject(data)) return false;
  const series = data as ConditionSeries;
  return series.resourceType === "Condition" && Array.isArray(series.resources);
}

export function isImmunizationSeries(data: unknown): data is ImmunizationSeries {
  if (!isValidObject(data)) return false;
  const series = data as ImmunizationSeries;
  return series.resourceType === "Immunization" && Array.isArray(series.resources);
}

export function isMedicationSeries(data: unknown): data is MedicationSeries {
  if (!isValidObject(data)) return false;
  const series = data as MedicationSeries;
  return series.resourceType === "Medication" && Array.isArray(series.resources);
}

export function isEnhancedMedicationSeries(data: unknown): data is EnhancedMedicationSeries {
  if (!isValidObject(data)) return false;
  const series = data as EnhancedMedicationSeries;
  return (
    series.resourceType === "Medication" &&
    Array.isArray(series.events) &&
    !!series.medicationWithRefs
  );
}

export function isProcedureSeries(data: unknown): data is ProcedureSeries {
  if (!isValidObject(data)) return false;
  const series = data as ProcedureSeries;
  return series.resourceType === "Procedure" && Array.isArray(series.resources);
}

// ============================================================================
// RAW FHIR RESOURCE TYPE GUARDS
// ============================================================================

export function isAllergyIntolerance(data: unknown): data is SmartAllergyIntolerance {
  if (!isValidObject(data)) return false;
  return (data as SmartAllergyIntolerance).resourceType === "AllergyIntolerance";
}

export function isDiagnosticReport(data: unknown): data is SmartDiagnosticReport {
  if (!isValidObject(data)) return false;
  return (data as SmartDiagnosticReport).resourceType === "DiagnosticReport";
}

export {
  isCondition,
  isFamilyMemberHistory,
  isImmunization,
  isMedication,
  isObservation,
  isProcedure,
};
